import { fetcher, useAuthFetch } from "matsuri-hooks"
import { useMemo } from "react"
import dayjs from "dayjs"
import {
    CalendarAvailability,
    RoomCalendar,
    RoomTypeCalendar
} from "../domain/calendar"
import {
    ExpiredDateFlag,
    ProprietaryManualTypes,
    convertManualTypesToRequest
} from "../domain/proprietary"
import { Reservation } from "../domain/reservation"
import { m2mCore_v1 } from "../endpoints/m2m-core.v1"

export const getExternalCalendar = (
    page: number,
    itemNum: number,
    nameQuery: string,
    startDate: string,
    endDate: string,
    tokenId: string,
    token: string
) => {
    return fetcher<ExternalCalendar>(
        m2mCore_v1.getRoomCalendarByTemporalToken({
            page,
            itemNum,
            nameQuery,
            startDate,
            endDate,
            tokenId,
            token
        }),
        {}
    )
}

export interface CreateManualReservationInput {
    listingId?: string
    roomTypeId: string
    staySpan: {
        startDate: string
        endDate: string
    }
    numberOfGuests: number
    manualType: string
    fixed: boolean
    memo: string
    ownerMemo: string
    stayType: string
    statusType?: string
    /** statusType が cancelled のときに必須。キャンセルになった日時(unix秒) */
    cancelledAt?: number
    totalPrice: {
        currencyType: string
        rawPrice: number
    }
    commissionFee: {
        currencyType: string
        rawPrice: number
    }
    ownSiteCommissionFee: {
        currencyType: string
        rawPrice: number
    }
    relocatedFrom?: string
}

export interface CreateManualReservationResponse {
    reservationId: string
}

export const requestCreateManualReservation = async (
    token: string,
    input: CreateManualReservationInput
) => {
    const resp = await fetcher<CreateManualReservationResponse>(
        m2mCore_v1.createRoomTypeManually({ id: input.roomTypeId }),
        {
            method: "POST",
            token,
            body: JSON.stringify(input)
        }
    )
    return resp
}

export const requestEditManualReservation = (
    token: string,
    input: CreateManualReservationInput & { otaId: string; accepted?: boolean }
) => {
    return fetcher(m2mCore_v1.editRoomTypeManually({ id: input.roomTypeId }), {
        method: "PUT",
        token,
        body: JSON.stringify(input)
    })
}

interface RequestUpdateOtaReservation {
    reservationId: string
    listingId: string
    fixed: boolean
    memo: string
    ownerMemo: string
    stayType: string
    commissionFee: {
        currencyType: string
        rawPrice: number
    }
    ownSiteCommissionFee: {
        currencyType: string
        rawPrice: number
    }
}

export const requestUpdateOtaReservation = (
    token: string,
    roomTypeId: string,
    input: RequestUpdateOtaReservation
) => {
    return fetcher(m2mCore_v1.updateOTAReservation({ id: roomTypeId }), {
        method: "PUT",
        token,
        body: JSON.stringify(input)
    })
}

export interface CreateProprietaryInput {
    listingId: string
    roomTypeId: string
    staySpan: {
        startDate: string
        endDate: string
    }
    totalPrice: number
    memo: string
    manualType: ProprietaryManualTypes
    expiredDate: string
    expiredDateFlag: ExpiredDateFlag
    submissionId: string
}

export const requestCreateProprietary = (
    token: string,
    input: CreateProprietaryInput
) => {
    return fetcher(
        m2mCore_v1.createRoomTypeProprietary({ id: input.roomTypeId }),
        {
            method: "POST",
            token,
            body: JSON.stringify({
                ...input,
                manualType: convertManualTypesToRequest(input.manualType)
            })
        }
    )
}

export interface EditProprietaryInput extends CreateProprietaryInput {
    id: string
}

export const requestEditProprietary = (
    token: string,
    input: EditProprietaryInput
) => {
    return fetcher(
        m2mCore_v1.editRoomTypeProprietary({ id: input.roomTypeId }),
        {
            method: "PUT",
            token,
            body: JSON.stringify({
                ...input,
                manualType: convertManualTypesToRequest(input.manualType)
            })
        }
    )
}

export const requestDeleteProprietary = (
    token: string,
    input: { roomTypeId: string; proprietaryId: string }
) => {
    return fetcher(
        m2mCore_v1.deleteRoomTypeProprietary({
            id: input.roomTypeId,
            proprietaryId: input.proprietaryId
        }),
        {
            method: "DELETE",
            token
        }
    )
}

export interface ExternalCalendar {
    availability: CalendarAvailability[]
}

export const useRoomTypeCalendar = (token: string, roomTypeId: string) => {
    return useAuthFetch<RoomTypeCalendar>(
        token,
        m2mCore_v1.getRoomTypeCalendar({ roomTypeId }),
        {}
    )
}

export const useRoomTypeCancelledReservation = (
    token: string,
    roomTypeId: string
): {
    stayedAndCancelledReservations: Reservation[]
    cancelOnTheDay: Reservation[]
    stayedAndCancelledReservationCount: number
    refetch: () => Promise<RoomTypeCalendar | undefined>
} => {
    const { data, refetch } = useRoomTypeCalendar(token, roomTypeId)
    const cancelledReservations = useMemo<Reservation[]>(() => {
        return (
            data?.reservations?.filter(
                reservation => reservation.stayed && reservation.cancelledAt > 0
            ) ?? []
        )
    }, [data?.reservations])

    const cancelOnTheDay = useMemo<Reservation[]>(
        () =>
            cancelledReservations.filter(
                reservation =>
                    dayjs.unix(reservation.cancelledAt).format("YYYY-MM-DD") ===
                    dayjs(reservation.staySpan.startDate).format("YYYY-MM-DD")
            ),
        [cancelledReservations]
    )
    const stayedAndCancelledReservations = useMemo<Reservation[]>(
        () =>
            cancelledReservations.filter(
                reservation =>
                    dayjs.unix(reservation.cancelledAt).format("YYYY-MM-DD") !==
                    dayjs(reservation.staySpan.startDate).format("YYYY-MM-DD")
            ),
        [cancelledReservations]
    )

    const stayedAndCancelledReservationCount = cancelledReservations.length

    return {
        stayedAndCancelledReservations,
        cancelOnTheDay,
        stayedAndCancelledReservationCount,
        refetch
    }
}

export const useRoomCalendar = (
    token: string,
    listingId: string,
    startDate: string,
    endDate: string
) => {
    return useAuthFetch<RoomCalendar>(
        token,
        m2mCore_v1.getListingCalendar({ startDate, endDate, id: listingId }),
        {}
    )
}

export interface ListingCalendarAvailability {
    availabilities: CalendarAvailability[]
    pagination: {
        total: number
    }
}

export const useListingCalendarAvailabilities = (
    token: string,
    input: {
        page: number
        itemsPerPage: number
        nameQuery: string
        startDate: string
        endDate: string
        filterBySpan: boolean
        listingIds: string
        prefectureId: string
        cityIds: string[]
        rest: string
    }
) => {
    return useAuthFetch<ListingCalendarAvailability>(
        token,
        m2mCore_v1.getCalendarAvailabilitiesByNameAndSpan({
            ...input,
            cityIds: input.cityIds.join(","),
            filterBySpan: input.filterBySpan ? "true" : "false"
        }),
        {}
    )
}

export const useListingExternalCalendarAvailabilitiesDelegation = (
    token: string,
    input: {
        page: number
        itemsPerPage: number
        nameQuery: string
        startDate: string
        endDate: string
        filterBySpan: boolean
        listingIds: string
    }
) => {
    return useAuthFetch<ListingCalendarAvailability>(
        token,
        m2mCore_v1.getExternalAvailabilitiyCalendarDelegationByNameAndSpan({
            ...input,
            filterBySpan: input.filterBySpan ? "true" : "false"
        }),
        {}
    )
}
